import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuthStore } from '../hooks/useAuth'

const fmt$ = (v: any) => {
  const n = parseFloat(v)
  if (isNaN(n)) return '$0.00'
  return n >= 0 ? `+$${n.toFixed(2)}` : `-$${Math.abs(n).toFixed(2)}`
}

const clr = (v: number) => v >= 0 ? 'var(--cash)' : 'var(--red)'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function TradeLog() {
  const { user } = useAuthStore()
  const isSuper = user?.tier === 'super'
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })

  // Equity trades from Alpaca (Mr. Roboto, manual, and retired managed-beta SPY/QQQ)
  const { data: equityData } = useQuery({
    queryKey: ['robo-trades'],
    queryFn: () => api.trading.orders('paper', 'all'),
    enabled: isSuper,
  })

  // Alpaca portfolio history — TRUE daily P&L across the account
  const { data: portfolioData } = useQuery({
    queryKey: ['portfolio-history-3m'],
    queryFn: () => api.trading.portfolio('3M'),
    enabled: isSuper,
    staleTime: 60_000,
  })

  const allTrades = (equityData?.orders ?? []).filter((o: any) => {
    if (o.status !== 'filled') return false
    // Crypto trading was retired — exclude any leftover crypto fills.
    const sym = o.symbol ?? ''
    if (sym.includes('BTC') || sym.includes('ETH') || sym.includes('DOGE') || sym.includes('SOL') || o.asset_class === 'crypto') return false
    return true
  }).map((o: any) => {
    const reason = o.client_order_id?.startsWith('robo_') ? 'Mr. Roboto'
      : o.client_order_id?.startsWith('eqbeta_') ? 'Trend strategy (retired)'
      : 'Manual'
    return {
      id:        o.id,
      symbol:    o.symbol,
      side:      o.side,
      qty:       parseFloat(o.filled_qty || o.qty),
      price:     parseFloat(o.filled_avg_price || 0),
      pnl:       null as number | null,
      status:    o.status,
      reason,
      createdAt: new Date(o.submitted_at).getTime() / 1000,
    }
  }).sort((a: any, b: any) => b.createdAt - a.createdAt)

  // PNL calendar — Alpaca portfolio history (real daily account P&L)
  const dailyPnl: Record<string, number> = {}
  const hist = portfolioData?.history
  if (hist?.timestamp?.length && hist?.profit_loss?.length) {
    for (let i = 0; i < hist.timestamp.length; i++) {
      const ts = hist.timestamp[i]
      const pl = hist.profit_loss[i]
      if (pl == null || pl === 0) continue
      const dateStr = new Date(ts * 1000).toISOString().slice(0, 10)
      dailyPnl[dateStr] = (dailyPnl[dateStr] ?? 0) + pl
    }
  }
  const [calYear, calMonthNum] = calMonth.split('-').map(Number)
  const daysInMonth = new Date(calYear, calMonthNum, 0).getDate()
  const firstDayOfWeek = new Date(calYear, calMonthNum - 1, 1).getDay()

  const totalPnl = Object.values(dailyPnl as Record<string, number>).reduce((s: number, v) => s + (v as number), 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Trade Log</h1>
        <div className="eyebrow">Equity trade history · {isSuper ? 'Super tier' : 'Upgrade for algo trading'}</div>
      </div>

      {!isSuper ? (
        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: 48, textAlign: 'center' }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.1rem', color: 'var(--amber)', marginBottom: 8 }}>Super Tier Required</div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>Algo trading logs require Super tier ($99.99/mo)</div>
        </div>
      ) : (
        <>
          {/* PNL Calendar */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="eyebrow">PNL Calendar</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => {
                  const d = new Date(calYear, calMonthNum - 2, 1)
                  setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
                }} style={{ background: 'none', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)', cursor: 'pointer', padding: '2px 8px', fontFamily: 'Syne Mono', fontSize: '.6rem' }}>←</button>
                <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.75rem', color: 'var(--text)', minWidth: 80, textAlign: 'center' }}>{MONTHS[calMonthNum - 1]} {calYear}</span>
                <button onClick={() => {
                  const d = new Date(calYear, calMonthNum, 1)
                  setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
                }} style={{ background: 'none', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)', cursor: 'pointer', padding: '2px 8px', fontFamily: 'Syne Mono', fontSize: '.6rem' }}>→</button>
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: clr(totalPnl), marginLeft: 8 }}>
                  Total: {fmt$(totalPnl)}
                </span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
              {['S','M','T','W','T','F','S'].map((d, i) => (
                <div key={i} style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', textAlign: 'center', padding: '2px 0' }}>{d}</div>
              ))}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1
                const dateStr = `${calYear}-${String(calMonthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                const pnl = (dailyPnl as Record<string, number>)[dateStr]
                const hasTrade = pnl !== undefined
                return (
                  <div key={day} style={{
                    padding: '6px 2px', textAlign: 'center', borderRadius: 2,
                    background: hasTrade ? (pnl >= 0 ? 'rgba(133,196,124,.12)' : 'rgba(224,92,92,.12)') : 'var(--paper)',
                    border: `1px solid ${hasTrade ? (pnl >= 0 ? 'rgba(133,196,124,.25)' : 'rgba(224,92,92,.25)') : 'var(--edge)'}`,
                  }}>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{day}</div>
                    {hasTrade && (
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.45rem', color: clr(pnl), marginTop: 1 }}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Trade list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allTrades.length === 0 && (
              <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: 32, textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>
                No trades yet — activate Mr. Roboto to start logging
              </div>
            )}
            {allTrades.map((t: any) => (
              <div key={t.id} style={{
                background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                {/* Type badge */}
                <span style={{
                  fontFamily: 'Syne Mono', fontSize: '.52rem', padding: '2px 6px', borderRadius: 2,
                  border: '1px solid var(--cash)', color: 'var(--cash)',
                }}>EQUITY</span>

                {/* Symbol */}
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', minWidth: 70 }}>{t.symbol}</div>

                {/* Side */}
                <span style={{
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '.65rem',
                  color: t.side === 'buy' ? 'var(--cash)' : 'var(--red)',
                }}>{t.side.toUpperCase()}</span>

                {/* Details */}
                <div style={{ flex: 1, fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                  {t.price ? `@ $${t.price.toFixed(2)}` : ''}{t.qty ? ` · ${t.qty.toFixed(4)} shares` : ''}
                  {t.reason && <span style={{ marginLeft: 6, opacity: .7 }}>· {t.reason}</span>}
                </div>

                {/* PNL */}
                {t.pnl != null && (
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.7rem', color: clr(t.pnl), fontWeight: 700 }}>
                    {fmt$(t.pnl)}
                  </span>
                )}

                {/* Status */}
                <span style={{
                  fontFamily: 'Syne Mono', fontSize: '.5rem', padding: '2px 6px', borderRadius: 2,
                  border: '1px solid var(--edge)', color: t.status === 'open' ? 'var(--amber)' : 'var(--muted)',
                }}>{t.status}</span>

                {/* Time */}
                <span style={{ fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', flexShrink: 0 }}>
                  {new Date(t.createdAt * 1000).toLocaleDateString()} {new Date(t.createdAt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
